/** @module application */

/**
 * Manages events for dynamically added DOM elements.
 * Must not be instantiated, use static method .singleton instead.
 */
export class LiveEvents {
    /**
     * @type {LiveEvents}
     */
    static #liveEventsInstance = null;
    /**
     * @type {boolean}
     */
    #debugSetting = false;
    /**
     * @type {WeakMap}
     */
    #boundEvents = new WeakMap();

    /**
     * @private
     */
    constructor({ debug = false } = {}) {
        if (LiveEvents.#liveEventsInstance !== null) {
            throw new Error('Do not instantiate. Use singleton.');
        }

        LiveEvents.#liveEventsInstance = this;

        if (process.env.NODE_ENV === 'production') {
            this.#debugSetting = false;
        } else {
            this.#debugSetting = debug;
        }
    }

    /**
     * Gets the global instance.
     * @param {object} options
     * @param {bool} [options.debug=false] - Whether debug statements should be printed. Ignored when in production mode.
     * @returns {LiveEvents}
     */
    static singleton(options = {}) {
        if (LiveEvents.#liveEventsInstance === null) {
            new LiveEvents(options);
        }

        return LiveEvents.#liveEventsInstance;
    }

    /**
     * Adds an event listener.
     * @param {string|Window|Node} selector - Selector to add the listener to.
     * @param {string} eventName - Name of the event to bind.
     * @param {function} listener - Handler to execute, when the event is triggered for an element matching the {@link selector}.
     * @param {object} [options=undefined] - Options for the {@link listener}.
     * @param {EventTarget} [container=document] - Container where to look for elements matching {@link selector}.
     * @returns {LiveEvents}
     */
    addEventListener(selector, eventName, listener, options = undefined, container = document) {
        // stores the event listeners in following hierarchy to only add one actual listener for each unique event:
        // container[] -> event[] -> options[] -> selectors[] -> listener[]
        this._addEvent(selector, eventName, listener, options, container);

        return this;
    }

    /**
     * Removes an event listener. Parameters must identically match the ones used for adding the listener via {@link LiveEvents.addEventListener}.
     * @param {string} selector - Selector to remove the listener from.
     * @param {string} eventName - Name of the event to remove.
     * @param {function} listener - Handler to remove.
     * @param {object} [options=undefined] - Options for the {@link listener}.
     * @param {EventTarget} [container=document] - Container where to look for elements matching {@link selector}.
     * @returns {boolean} Whether a listener was successfully removed.
     */
    removeEventListener(selector, eventName, listener, options = undefined, container = document) {
        return this._removeEvent(selector, eventName, listener, options, container);
    }

    /**
     * @private
     */
    _attachListenerToDOM(selector, eventName, listener, options, container) {
        container.addEventListener(
            eventName,
            (event) => {
                let selectors = this.#boundEvents.get(container).get(eventName).get(JSON.stringify(options));

                if (this.#debugSetting) {
                    console.group('[LiveEvents]');
                    console.log(`Event '${eventName}' triggered from target:`, event.target);
                    console.log('Listener options:', options);
                    console.log('Registered selectors:', selectors);
                }

                if (selectors) {
                    selectors.forEach((listeners, selector) => {
                        if (
                            (typeof selector === 'string' && event.target.closest(selector)) ||
                            (selector instanceof Node &&
                                (event.target === selector || selector.contains(event.target))) ||
                            selector instanceof Window
                        ) {
                            if (this.#debugSetting) {
                                console.log('Matching selector:', selector);
                            }

                            if (listeners) {
                                listeners.forEach((listener) => {
                                    listener.apply(container, [event, selector]);

                                    if (options && options.once) {
                                        this._removeEvent(selector, eventName, listener, options, container);
                                    }
                                });
                            }
                        } else {
                            if (this.#debugSetting) {
                                console.log('Matching selector:', 'none');
                            }
                        }
                    });
                }

                if (this.#debugSetting) {
                    console.groupEnd();
                }
            },
            options,
        );
    }

    /**
     * @private
     */
    _addListener(selectors, eventName, listener, options, container) {
        let selectorsMap = this.#boundEvents.get(container).get(eventName).get(options);

        if (Array.isArray(selectors) === false) {
            selectors = [selectors];
        }

        selectors.forEach((selector) => {
            if (selectorsMap.has(selector) === false) {
                selectorsMap.set(selector, new Set());
            }

            selectorsMap.get(selector).add(listener);
        });
    }

    /**
     * @private
     */
    _addSelector(selector, eventName, listener, options, container) {
        let optionsMap = this.#boundEvents.get(container).get(eventName),
            optionsStr = JSON.stringify(options);

        if (optionsMap.has(optionsStr) === false) {
            optionsMap.set(optionsStr, new Map());

            this._attachListenerToDOM(selector, eventName, listener, options, container);
        }

        this._addListener(selector, eventName, listener, optionsStr, container);
    }

    /**
     * @private
     */
    _addOptions(selector, eventName, listener, options, container) {
        let eventsMap = this.#boundEvents.get(container);

        if (eventsMap.has(eventName) === false) {
            eventsMap.set(eventName, new Map());
        }

        this._addSelector(selector, eventName, listener, options, container);
    }

    /**
     * @private
     */
    _addEvent(selector, eventName, listener, options, container) {
        if (this.#boundEvents.has(container) === false) {
            this.#boundEvents.set(container, new Map());
        }

        this._addOptions(selector, eventName, listener, options, container);
    }

    /**
     * @private
     */
    _removeEvent(selector, eventName, listener, options, container) {
        let events = this.#boundEvents.get(container),
            options_ = events && events.get(eventName),
            selectors = options_ && options_.get(JSON.stringify(options)),
            listeners = selectors && selectors.get(selector);

        if (listeners && listeners.has(listener)) {
            listeners.delete(listener);
            container.removeEventListener(eventName, listener, options);

            return true;
        }

        return false;
    }
}
